import { useTranslation } from "react-i18next";
import { formatMoney } from "@/lib/currency";
import NumberRollup from "@/components/NumberRollup";
import { Activity, ArrowDownRight, ArrowUpRight, PiggyBank, Wallet } from "lucide-react";

/**
 * OverviewGrid — the north-star row + the 4-stat monthly row.
 * Two grids that always render side-by-side above the charts.
 * Extracted from Dashboard.jsx in iter-31.
 */
export default function OverviewGrid({ summary, currency }) {
  const { t } = useTranslation();
  const cur = currency;
  return (
    <>
      {/* North Star */}
      <div className="grid lg:grid-cols-4 gap-5 mb-6 stagger">
        <div className="lg:col-span-2 card-surface p-7 relative overflow-hidden tilt shadow-soft">
          <div className="label-eyebrow">{t("dashboard_body.stats.net_worth")}</div>
          <div className="num-hero mt-3" data-testid="dashboard-net-worth">
            <NumberRollup value={summary.net_worth} currency={cur} />
          </div>
          <div className="mt-2 text-sm text-[var(--text-secondary)]">
            {t("dashboard_body.stats.net_worth_breakdown", {
              investments: formatMoney(summary.total_investment_value, cur),
              savings: formatMoney(summary.total_savings, cur),
              debt: formatMoney(summary.total_loan_outstanding, cur),
            })}
          </div>
          <div className="mt-6 grid grid-cols-3 gap-4">
            <MicroStat
              label={t("dashboard_body.stats.health_score")}
              value={`${summary.health_score}/100`}
              tone={summary.health_score >= 70 ? "pos" : summary.health_score >= 40 ? "warn" : "neg"}
              testId="dashboard-health-score"
            />
            <MicroStat
              label={t("dashboard_body.stats.savings_rate")}
              value={`${summary.savings_rate}%`}
              tone={summary.savings_rate >= 20 ? "pos" : summary.savings_rate >= 0 ? "warn" : "neg"}
            />
            <MicroStat
              label={t("dashboard_body.stats.gain_inv")}
              value={formatMoney(summary.investment_gain, cur)}
              tone={summary.investment_gain >= 0 ? "pos" : "neg"}
            />
          </div>
        </div>

        <StatCard
          label={t("dashboard_body.stats.monthly_cashflow")}
          value={formatMoney(summary.monthly_cashflow, cur)}
          icon={<Activity size={18}/>}
          tone={summary.monthly_cashflow >= 0 ? "pos" : "neg"}
          testId="dashboard-cashflow"
        />
        <StatCard
          label={t("dashboard_body.stats.loan_outstanding")}
          value={formatMoney(summary.total_loan_outstanding, cur)}
          icon={<PiggyBank size={18}/>}
          tone="warn"
          testId="dashboard-loans"
        />
      </div>
    </>
  );
}

export function MonthlyStatRow({ summary, currency }) {
  const { t } = useTranslation();
  const cur = currency;
  return (
    <div className="grid lg:grid-cols-4 gap-5 mb-6 stagger">
      <StatCard label={t("dashboard_body.stats.monthly_income")}  value={formatMoney(summary.monthly_income, cur)}  icon={<ArrowUpRight size={18}/>}    tone="pos" />
      <StatCard label={t("dashboard_body.stats.monthly_expense")} value={formatMoney(summary.monthly_expense, cur)} icon={<ArrowDownRight size={18}/>}  tone="neg" />
      <StatCard label={t("dashboard_body.stats.monthly_emi")}     value={formatMoney(summary.monthly_emi, cur)}     icon={<PiggyBank size={18}/>}       tone="warn" />
      <StatCard label={t("dashboard_body.stats.monthly_subs")}    value={formatMoney(summary.monthly_subs, cur)}    icon={<Wallet size={18}/>}          tone="warn" />
    </div>
  );
}

function StatCard({ label, value, icon, tone = "neutral", testId }) {
  const cls = tone === "pos"
    ? "text-[var(--accent-positive)]"
    : tone === "warn"
      ? "text-[#A16A25]"
      : tone === "neg"
        ? "text-[var(--accent-danger)]"
        : "text-[var(--text-primary)]";
  return (
    <div className="card-surface p-5">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{label}</div>
        <div className="text-[var(--text-secondary)]">{icon}</div>
      </div>
      <div className={`num-lg mt-3 ${cls}`} data-testid={testId}>{value}</div>
    </div>
  );
}

function MicroStat({ label, value, tone, testId }) {
  const cls = tone === "pos"
    ? "text-[var(--accent-positive)]"
    : tone === "warn"
      ? "text-[#A16A25]"
      : tone === "neg"
        ? "text-[var(--accent-danger)]"
        : "text-[var(--text-primary)]";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">{label}</div>
      <div className={`num-md mt-1 ${cls}`} data-testid={testId}>{value}</div>
    </div>
  );
}
